import { supabase } from "./supabase";
import { syncService } from "./syncService";
import { getGuestId, rotateGuestId } from "./privacy/ownerStorage";
export interface AuthUser {
  id: string;
  email: string | null;
  isAnonymous: boolean;
}
export type AuthStatus =
  "loading" | "authenticated" | "unauthenticated" | "anonymous" | "offline";
class AuthService {
  private user: AuthUser | null = null;
  private status: AuthStatus = "loading";
  private listeners = new Set<
    (user: AuthUser | null, status: AuthStatus) => void
  >();
  constructor() {
    void this.initialize();
  }
  private guest(fresh = false) {
    this.setUser(
      {
        id: fresh ? rotateGuestId() : getGuestId(),
        email: null,
        isAnonymous: true,
      },
      "anonymous",
    );
  }
  private async initialize() {
    if (!supabase) {
      this.guest();
      return;
    }
    try {
      const { data, error } = await supabase.auth.getUser();
      if (error || !data.user) await this.offlineIdentity();
      else
        this.setUser(
          {
            id: data.user.id,
            email: data.user.email ?? null,
            isAnonymous: Boolean(data.user.is_anonymous),
          },
          data.user.is_anonymous ? "anonymous" : "authenticated",
        );
    } catch {
      await this.offlineIdentity();
    }
    const verifyOnline = () => {
      void this.verifySession();
    };
    window.addEventListener("online", verifyOnline);
    supabase.auth.onAuthStateChange((event, session) => {
      if (session?.user)
        this.setUser(
          {
            id: session.user.id,
            email: session.user.email ?? null,
            isAnonymous: Boolean(session.user.is_anonymous),
          },
          session.user.is_anonymous ? "anonymous" : "authenticated",
        );
      else if (event === "SIGNED_OUT") this.guest(true);
      else this.guest();
    });
  }
  private async offlineIdentity() {
    const session = supabase ? await supabase.auth.getSession().catch(() => null) : null;
    const local = session?.data.session?.user;
    if (local && !local.is_anonymous)
      this.setUser(
        { id: local.id, email: local.email || null, isAnonymous: false },
        "offline",
      );
    else this.guest();
  }
  private async verifySession() {
    if (!supabase) return;
    try {
      const { data, error } = await supabase.auth.getUser();
      if (!error && data.user && !data.user.is_anonymous)
        this.setUser(
          {
            id: data.user.id,
            email: data.user.email || null,
            isAnonymous: false,
          },
          "authenticated",
        );
    } catch {
      /* Offline local practice continues without cloud authority. */
    }
  }
  private setUser(user: AuthUser, status: AuthStatus) {
    if (this.user?.id === user.id && this.status === status) return;
    this.user = user;
    this.status = status;
    syncService.setIdentity(user.id, status === "authenticated");
    this.listeners.forEach((listener) => listener(user, status));
  }
  getUser() {
    return this.user;
  }
  getStatus() {
    return this.status;
  }
  onAuthChange(callback: (user: AuthUser | null, status: AuthStatus) => void) {
    this.listeners.add(callback);
    callback(this.user, this.status);
    return () => this.listeners.delete(callback);
  }
  async sendMagicLink(email: string): Promise<{ error: Error | null }> {
    if (!supabase)
      return {
        error: new Error(
          "Cloud accounts are not configured. Local practice is available.",
        ),
      };
    try {
      const { error } = await supabase.auth.signInWithOtp({
        email,
        options: {
          emailRedirectTo: `${location.origin}/`,
          shouldCreateUser: true,
        },
      });
      return { error };
    } catch {
      return {
        error: new Error(
          "Could not send sign-in link. Try again when connected.",
        ),
      };
    }
  }
  async signOut(): Promise<void> {
    // Switch visible state immediately; outstanding work keeps its original owner.
    this.guest(true);
    if (supabase) {
      const { error } = await supabase.auth.signOut();
      if (error)
        throw new Error(
          "Local session ended. Remote sign-out failed; close this shared browser and retry when connected.",
        );
    }
  }
  startAnonymousSession() {
    this.guest(true);
  }
  linkToEmail(email: string) {
    return this.sendMagicLink(email);
  }
  canAccessOrgFeatures() {
    return this.status === "authenticated";
  }
}
export const authService = new AuthService();
